import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";

export const PAGE_BACKGROUND_PRESETS = [
  { id: "blue", name: "Blue", color: "#68A4F2" },
  { id: "mint", name: "Mint", color: "#72C9A5" },
  { id: "lavender", name: "Lavender", color: "#A99BE8" },
  { id: "rose", name: "Rose", color: "#E99AAA" },
  { id: "cream", name: "Cream", color: "#E8C56A" },
  { id: "gray", name: "Gray", color: "#AEB9C5" },
];

export const DEFAULT_PAGE_BACKGROUND = PAGE_BACKGROUND_PRESETS[0].color;

const PRESET_COLORS = new Set(PAGE_BACKGROUND_PRESETS.map((preset) => preset.color));
const AppearanceContext = createContext(null);

export const useAppearance = () => {
  const value = useContext(AppearanceContext);
  if (!value) {
    throw new Error("useAppearance must be used within AppearanceProvider");
  }
  return value;
};

function appearanceStorageKey(userId) {
  return `emrbymd.v3.user.appearance.${userId}`;
}

function appearanceUserId(user) {
  if (!user || typeof user !== "object") return null;
  return user.id || user.email || null;
}

function readPreference(userId) {
  if (!userId) return { enabled: false, color: DEFAULT_PAGE_BACKGROUND };
  try {
    const raw = localStorage.getItem(appearanceStorageKey(userId));
    if (!raw) return { enabled: false, color: DEFAULT_PAGE_BACKGROUND };
    const parsed = JSON.parse(raw);
    const color = PRESET_COLORS.has(parsed?.color) ? parsed.color : DEFAULT_PAGE_BACKGROUND;
    return { enabled: !!parsed?.enabled, color };
  } catch {
    return { enabled: false, color: DEFAULT_PAGE_BACKGROUND };
  }
}

function writePreference(userId, preference) {
  if (!userId) return;
  localStorage.setItem(appearanceStorageKey(userId), JSON.stringify(preference));
}

export function AppearanceProvider({ children }) {
  const { user } = useAuth();
  const userId = appearanceUserId(user);
  const [enabled, setEnabledState] = useState(false);
  const [color, setColorState] = useState(DEFAULT_PAGE_BACKGROUND);

  useEffect(() => {
    const next = readPreference(userId);
    setEnabledState(next.enabled);
    setColorState(next.color);
  }, [userId]);

  const persist = useCallback((nextEnabled, nextColor) => {
    writePreference(userId, { enabled: nextEnabled, color: nextColor });
  }, [userId]);

  const setEnabled = useCallback((nextEnabled) => {
    setEnabledState(nextEnabled);
    persist(nextEnabled, color);
  }, [color, persist]);

  const selectColor = useCallback((nextColor) => {
    const resolved = PRESET_COLORS.has(nextColor) ? nextColor : DEFAULT_PAGE_BACKGROUND;
    setColorState(resolved);
    persist(true, resolved);
    setEnabledState(true);
  }, [persist]);

  const canvasFor = useCallback((defaultColor) => (
    enabled ? color : defaultColor
  ), [color, enabled]);

  const value = useMemo(() => ({
    enabled,
    color,
    presets: PAGE_BACKGROUND_PRESETS,
    setEnabled,
    selectColor,
    canvasFor,
  }), [canvasFor, color, enabled, selectColor, setEnabled]);

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
}
